/**
 * field_value is stored verbatim. Most rows are plain strings, but the request
 * shaping keys hold "placeholder JSON" - an object whose keys all start with #
 * that the collector substitutes at run time.
 *
 * parseParam / buildParam let the add-object form offer a guided editor for
 * that shape while still round-tripping any key it does not model, so no value
 * is ever silently dropped.
 */

export type ParamMode =
  | "value"
  | "values"
  | "keeper"
  | "user_input"
  | "view"
  | "dateformat"
  | "widget";

export const PARAM_MODES: { value: ParamMode; label: string }[] = [
  { value: "value", label: "single #value" },
  { value: "values", label: "#values list" },
  { value: "keeper", label: "keeper vault" },
  { value: "user_input", label: "runtime date" },
  { value: "view", label: "data_collector_view field" },
  { value: "dateformat", label: "#dateformat" },
  { value: "widget", label: "selenium element" },
];

export interface ParsedParam {
  name: string;
  mode: ParamMode;
  val: string;
  hasValue: boolean;
  values: string[];
  keeper: string;
  kind: string;
  hasKind: boolean;
  input: string;
  fmt: string;
  field: string;
  dateformat: string;
  type: string;
  text: string;
  hasText: boolean;
  hasName: boolean;
  /** keys the guided editor does not model, preserved as-is */
  extra: { [key: string]: any };
}

const MODELLED_KEYS = [
  "#name",
  "#value",
  "#values",
  "#source",
  "#keeper_name",
  "#kind",
  "#input_name",
  "#format",
  "#table_field",
  "#dateformat",
  "#type",
  "#text",
];

export function parseParam(value: string): ParsedParam | null {
  let parsed: any;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  // payload / gdrive_file_details / fetch_by_brand are nested structures, not
  // placeholder JSON - they stay raw
  if (!Object.keys(parsed).some((key) => key.charAt(0) === "#")) return null;

  const extra: { [key: string]: any } = {};
  Object.keys(parsed).forEach((key) => {
    if (!MODELLED_KEYS.includes(key)) extra[key] = parsed[key];
  });

  let mode: ParamMode = "value";
  if (parsed["#type"] !== undefined) mode = "widget";
  else if (parsed["#values"] !== undefined) mode = "values";
  else if (parsed["#source"] === "keeper") mode = "keeper";
  else if (parsed["#source"] === "user_input") mode = "user_input";
  else if (parsed["#source"] === "data_collector_view") mode = "view";
  else if (parsed["#dateformat"] !== undefined) mode = "dateformat";

  return {
    name: parsed["#name"] || "",
    mode,
    val: parsed["#value"] !== undefined ? String(parsed["#value"]) : "",
    hasValue: parsed["#value"] !== undefined,
    values: Array.isArray(parsed["#values"])
      ? parsed["#values"].map(String)
      : parsed["#values"] !== undefined
        ? [String(parsed["#values"])]
        : [""],
    keeper: parsed["#keeper_name"] || "",
    kind: parsed["#kind"] || "",
    hasKind: parsed["#kind"] !== undefined,
    input: parsed["#input_name"] || "",
    fmt: parsed["#format"] || "",
    field: parsed["#table_field"] || "",
    dateformat: parsed["#dateformat"] || "",
    type: parsed["#type"] || "",
    text: parsed["#text"] || "",
    hasText: parsed["#text"] !== undefined,
    hasName: parsed["#name"] !== undefined,
    extra,
  };
}

export function buildParam(param: ParsedParam): string {
  const out: { [key: string]: any } = {};

  if (param.mode === "widget") {
    out["#type"] = param.type;
    out["#value"] = param.val;
    if (param.text || param.hasText) out["#text"] = param.text;
    if (param.hasName) out["#name"] = param.name;
    Object.keys(param.extra || {}).forEach((key) => {
      out[key] = param.extra[key];
    });
    return JSON.stringify(out);
  }

  out["#name"] = param.name;
  if (param.mode === "value" && (param.hasValue || param.val !== "")) {
    out["#value"] = param.val;
  } else if (param.mode === "values") {
    out["#values"] = (param.values || []).filter((entry) => entry !== "");
  } else if (param.mode === "keeper") {
    out["#source"] = "keeper";
    out["#keeper_name"] = param.keeper;
    if (param.kind || param.hasKind) out["#kind"] = param.kind;
  } else if (param.mode === "user_input") {
    out["#source"] = "user_input";
    out["#input_name"] = param.input;
    out["#format"] = param.fmt;
  } else if (param.mode === "view") {
    out["#source"] = "data_collector_view";
    out["#table_field"] = param.field;
  } else if (param.mode === "dateformat") {
    out["#dateformat"] = param.dateformat;
  }

  // never drop keys the editor does not model
  Object.keys(param.extra || {}).forEach((key) => {
    out[key] = param.extra[key];
  });
  return JSON.stringify(out);
}

/** The default the guided builder starts from when the value was plain text. */
export const EMPTY_PARAM_JSON = JSON.stringify({ "#name": "", "#value": "" });

/**
 * A JSON object with no #-prefixed key is a nested structure the guided builder
 * cannot model - the form keeps it as raw JSON and says so.
 */
export function isStructuralJson(value: string): boolean {
  try {
    const parsed = JSON.parse(value);
    return (
      !!parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      !Object.keys(parsed).some((key) => key.charAt(0) === "#")
    );
  } catch {
    return false;
  }
}
